import { useEffect, useState } from "react";
import EmailGate from "../components/EmailGate";
import { hasStoredContentAccess } from "../lib/gatedContentTracking";

const EXPECTATIONS_ACCESS_KEY = "asha_expectations_guide_access";

const STYLES = `
.guide-doc {
  --paper: #ffffff;
  --paper-2: #f6f9ff;
  --ink: #0f172a;
  --ink-2: #334155;
  --ink-3: #64748b;
  --ink-4: #94a3b8;
  --line: #e2e8f0;
  --line-2: #eef2f7;
  --line-3: #f1f5fb;
  --accent: #2563eb;
  --accent-deep: #1d4ed8;
  --accent-soft: #eff6ff;
  --accent-ink: #1e40af;
  --accent-border: #bfdbfe;

  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: var(--ink);
  font-size: 15.5px;
  line-height: 1.7;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

.guide-doc *,
.guide-doc *::before,
.guide-doc *::after { box-sizing: border-box; }

.guide-doc a { color: var(--accent); text-decoration: none; }
.guide-doc a:hover { text-decoration: underline; }

.guide-doc .page {
  max-width: 880px;
  margin: 56px auto;
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 14px;
  box-shadow:
    0 1px 0 rgba(15,23,42,0.02),
    0 30px 60px -30px rgba(15,23,42,0.18),
    0 12px 24px -16px rgba(15,23,42,0.10);
  overflow: hidden;
}

/* TOP BAR */
.guide-doc .topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 56px;
  border-bottom: 1px solid var(--line);
  background: var(--paper);
}
.guide-doc .brand {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  font-size: 15px;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.guide-doc .brand-mark {
  width: 26px; height: 26px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
}
.guide-doc .brand-mark img { width: 100%; height: 100%; display: block; object-fit: cover; }
.guide-doc .brand-meta { color: var(--ink-3); font-weight: 400; }
.guide-doc .pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent-ink);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border: 1px solid var(--accent-border);
}
.guide-doc .pill::before {
  content: "";
  width: 6px; height: 6px;
  border-radius: 99px;
  background: var(--accent);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
}

/* HERO */
.guide-doc .hero {
  padding: 72px 56px 48px;
  border-bottom: 1px solid var(--line);
  position: relative;
  background:
    radial-gradient(900px 280px at 90% -80px, rgba(191, 219, 254, 0.5), transparent 60%),
    radial-gradient(700px 240px at 0% 0%, rgba(239, 246, 255, 0.7), transparent 60%),
    var(--paper);
}
.guide-doc .eyebrow {
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 22px;
  font-weight: 600;
}
.guide-doc .eyebrow b { color: var(--accent-ink); font-weight: 600; }

.guide-doc h1.title {
  font-weight: 700;
  font-size: 56px;
  line-height: 1.04;
  letter-spacing: -0.032em;
  margin: 0 0 18px;
  color: var(--ink);
  max-width: 26ch;
  text-wrap: balance;
}
.guide-doc h1.title em {
  color: var(--accent);
  font-style: normal;
}
.guide-doc .title-sub {
  font-size: 18px;
  color: var(--ink-2);
  margin: 0 0 32px;
  max-width: 42ch;
  font-weight: 400;
}
.guide-doc .lede {
  font-size: 17px;
  line-height: 1.65;
  color: var(--ink-2);
  max-width: 64ch;
  margin: 0 0 40px;
  text-wrap: pretty;
}

.guide-doc .meta-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
}
.guide-doc .meta-cell {
  padding: 18px 22px 18px 0;
  border-right: 1px solid var(--line-2);
}
.guide-doc .meta-cell:not(:first-child) { padding-left: 22px; }
.guide-doc .meta-cell:last-child { border-right: 0; padding-right: 0; }
.guide-doc .meta-label {
  font-size: 10.5px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 8px;
  font-weight: 600;
}
.guide-doc .meta-value {
  font-size: 17px;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -0.01em;
}
.guide-doc .meta-value small {
  display: block;
  font-size: 12.5px;
  color: var(--ink-3);
  font-weight: 400;
  margin-top: 4px;
  letter-spacing: 0;
}

/* SECTION */
.guide-doc section.s {
  padding: 56px 56px 60px;
  border-bottom: 1px solid var(--line);
}
.guide-doc section.s:last-of-type { border-bottom: 0; }
.guide-doc .s-eyebrow {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}
.guide-doc .s-num {
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent-ink);
  font-weight: 600;
  padding: 3px 9px;
  background: var(--accent-soft);
  border: 1px solid var(--accent-border);
  border-radius: 5px;
}
.guide-doc .s-rule {
  flex: 1;
  height: 1px;
  background: var(--line);
}
.guide-doc .s-title {
  font-size: 30px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.18;
  color: var(--ink);
  margin: 0 0 14px;
  max-width: 28ch;
  text-wrap: balance;
}
.guide-doc .s-sub {
  font-size: 17px;
  color: var(--ink-2);
  margin: 0 0 28px;
  max-width: 64ch;
  line-height: 1.6;
}

.guide-doc p {
  font-size: 16px;
  line-height: 1.7;
  color: var(--ink-2);
  margin: 0 0 18px;
  max-width: 68ch;
  text-wrap: pretty;
}
.guide-doc p:last-child { margin-bottom: 0; }
.guide-doc strong { color: var(--ink); font-weight: 600; }
.guide-doc em.accent {
  color: var(--accent-ink);
  background: var(--accent-soft);
  font-style: normal;
  padding: 1px 6px;
  border-radius: 4px;
  font-weight: 500;
}

.guide-doc h3.sub {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin: 32px 0 8px;
  display: flex;
  align-items: baseline;
  gap: 10px;
}
.guide-doc h3.sub::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 99px;
  background: var(--accent);
  display: inline-block;
  margin-right: 4px;
  flex-shrink: 0;
}
.guide-doc h3.sub small {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent-ink);
}

/* Simple lists - prose-style */
.guide-doc ul.simple {
  list-style: none;
  padding: 0;
  margin: 0 0 22px;
}
.guide-doc ul.simple li {
  padding: 4px 0 4px 22px;
  position: relative;
  font-size: 16px;
  line-height: 1.7;
  color: var(--ink-2);
  max-width: 68ch;
}
.guide-doc ul.simple li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 14px;
  width: 6px; height: 6px;
  border-radius: 99px;
  background: var(--accent);
  opacity: 0.7;
}
.guide-doc ul.simple li b { color: var(--ink); font-weight: 600; }

/* Definition-style list (label — text on a line) */
.guide-doc dl.dl {
  margin: 0 0 22px;
  padding: 0;
  border-top: 1px solid var(--line-2);
}
.guide-doc dl.dl > div {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 28px;
  padding: 14px 0;
  border-bottom: 1px solid var(--line-2);
  align-items: baseline;
}
.guide-doc dl.dl dt {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -0.005em;
}
.guide-doc dl.dl dt small {
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent-ink);
  margin-top: 4px;
}
.guide-doc dl.dl dd {
  margin: 0;
  font-size: 15.5px;
  color: var(--ink-2);
  line-height: 1.65;
}

/* Pull quote */
.guide-doc blockquote.pull {
  margin: 28px 0;
  padding: 18px 0 18px 22px;
  border-left: 3px solid var(--accent);
  font-size: 18px;
  font-style: italic;
  color: var(--ink);
  line-height: 1.55;
  max-width: 60ch;
  font-weight: 400;
}

/* Summary table - very minimal, just rules */
.guide-doc table.summary {
  width: 100%;
  border-collapse: collapse;
  margin: 4px 0 0;
}
.guide-doc table.summary thead th {
  text-align: left;
  padding: 0 0 12px;
  font-size: 10.5px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-3);
  font-weight: 600;
  border-bottom: 1px solid var(--ink);
}
.guide-doc table.summary tbody td {
  padding: 16px 0;
  border-bottom: 1px solid var(--line-2);
  vertical-align: top;
}
.guide-doc table.summary tbody tr:last-child td { border-bottom: 0; }
.guide-doc table.summary .col-when { width: 32%; padding-right: 24px; }
.guide-doc table.summary .col-detail { width: 68%; }
.guide-doc table.summary .sm-when {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -0.005em;
}
.guide-doc table.summary .sm-detail {
  font-size: 15px;
  color: var(--ink-2);
  line-height: 1.65;
}

/* FAQ */
.guide-doc .faq-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
  border-top: 1px solid var(--line-2);
  padding-top: 24px;
  margin-top: 4px;
}
.guide-doc .faq-item {
  padding-bottom: 24px;
  border-bottom: 1px dashed var(--line-2);
}
.guide-doc .faq-item:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}
.guide-doc .faq-q {
  font-size: 18px;
  font-weight: 600;
  color: var(--ink);
  margin: 0 0 10px;
  letter-spacing: -0.01em;
  line-height: 1.4;
}
.guide-doc .faq-a {
  font-size: 16px;
  line-height: 1.7;
  color: var(--ink-2);
  margin: 0;
  max-width: 72ch;
}
.guide-doc .faq-a strong { color: var(--ink); font-weight: 600; }

/* Tail */
.guide-doc .tail {
  padding: 22px 56px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--paper-2);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  border-top: 1px solid var(--line);
  font-weight: 500;
}

/* Responsive */
@media (max-width: 820px) {
  .guide-doc .page { margin: 24px; }
  .guide-doc .hero,
  .guide-doc section.s,
  .guide-doc .topbar,
  .guide-doc .tail { padding-left: 28px; padding-right: 28px; }
  .guide-doc h1.title { font-size: 36px; max-width: none; }
  .guide-doc .s-title { font-size: 24px; }
  .guide-doc .meta-grid { grid-template-columns: 1fr 1fr; }
  .guide-doc .meta-cell { padding: 16px 0 !important; border-right: 0; border-bottom: 1px solid var(--line-2); }
  .guide-doc .meta-cell:nth-child(2n) { padding-left: 22px !important; border-left: 1px solid var(--line-2); }
  .guide-doc .meta-cell:nth-last-child(-n+2) { border-bottom: 0; }
  .guide-doc dl.dl > div { grid-template-columns: 1fr; gap: 4px; }
}

@media (max-width: 560px) {
  .guide-doc .page { margin: 14px; border-radius: 14px; }
  .guide-doc .hero,
  .guide-doc section.s,
  .guide-doc .topbar,
  .guide-doc .tail { padding-left: 22px; padding-right: 22px; }
  .guide-doc .topbar { padding-top: 14px; padding-bottom: 14px; }
  .guide-doc .hero { padding-top: 44px; padding-bottom: 36px; }
  .guide-doc section.s { padding-top: 40px; padding-bottom: 44px; }
  .guide-doc h1.title { font-size: 30px; }
  .guide-doc .lede { font-size: 15.5px; }
  .guide-doc .meta-grid { grid-template-columns: 1fr; }
  .guide-doc .meta-cell { padding-left: 0 !important; border-left: 0 !important; }
  .guide-doc .meta-cell:not(:last-child) { border-bottom: 1px solid var(--line-2); }
  .guide-doc table.summary,
  .guide-doc table.summary tbody,
  .guide-doc table.summary tr,
  .guide-doc table.summary td { display: block; width: auto !important; }
  .guide-doc table.summary thead { display: none; }
  .guide-doc table.summary tr {
    padding: 16px 0;
    border-bottom: 1px solid var(--line-2);
  }
  .guide-doc table.summary tbody tr:last-child { border-bottom: 0; }
  .guide-doc table.summary td { padding: 0 !important; border: 0 !important; }
  .guide-doc table.summary td + td { margin-top: 6px; }
  .guide-doc .tail {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding-top: 16px; padding-bottom: 16px;
  }
}

@media print {
  @page { size: letter; margin: 0.4in; }
  body, html { background: #fff !important; }
  body.guide-body { background: #fff !important; }
  .guide-doc { font-size: 11.5px !important; }
  .guide-doc .page {
    max-width: 100% !important;
    margin: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
  .guide-doc .topbar { padding: 14px 24px; }
  .guide-doc .hero { padding: 32px 24px 24px; }
  .guide-doc h1.title { font-size: 28px; max-width: none; }
  .guide-doc .lede { font-size: 12.5px; }
  .guide-doc section.s { padding: 24px 24px 28px; }
  .guide-doc .s-title { font-size: 18px; }
  .guide-doc .tail { padding: 12px 24px; }
  .guide-doc h3.sub, .guide-doc .s-title, .guide-doc h1.title {
    break-after: avoid;
    page-break-after: avoid;
  }
  .guide-doc dl.dl > div, .guide-doc table.summary tr, .guide-doc blockquote.pull {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
}
`;

const ExpectationsGuidePage = () => {
  const [isLocked, setIsLocked] = useState(true);

  useEffect(() => {
    if (
      hasStoredContentAccess(
        EXPECTATIONS_ACCESS_KEY,
        "asha_expectations_guide_session_id"
      )
    ) {
      setIsLocked(false);
    }
  }, []);

  const handleAccessGranted = (email) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(EXPECTATIONS_ACCESS_KEY, "true");
    }
    // eslint-disable-next-line no-console
    console.log("Expectations guide access granted for:", email);
    setIsLocked(false);
  };

  useEffect(() => {
    const previousBg = document.body.style.background;
    const previousMargin = document.body.style.margin;
    document.body.style.background = "#eef3fb";
    document.body.style.margin = "0";
    document.body.classList.add("guide-body");
    document.title = "Asha — Setting Expectations Guide";

    return () => {
      document.body.style.background = previousBg;
      document.body.style.margin = previousMargin;
      document.body.classList.remove("guide-body");
    };
  }, []);

  return (
    <>
      {isLocked && (
        <EmailGate
          onAccessGranted={handleAccessGranted}
          title="Please enter your email to view the expectations guide"
          description={
            <>
              Access to the{" "}
              <span className="text-blue-400 font-semibold">Asha Health</span>{" "}
              setting expectations guide is reserved for our leads and customers.
            </>
          }
          submitLabel="View guide"
          agreementNoun="this guide"
          confidentialityText="The information in this guide is intended for prospective and existing Asha Health customers. Sharing without prior written consent from Asha Health, Inc. is prohibited."
          sessionStorageKey="asha_expectations_guide_session_id"
        />
      )}

      <div className="guide-doc">
        <style dangerouslySetInnerHTML={{ __html: STYLES }} />

        <div className="page">
        {/* TOP BAR */}
        <div className="topbar">
          <div className="brand">
            <div className="brand-mark">
              <img src="/assets/alogo.png" alt="Asha" />
            </div>
            <span>Asha Health</span>
            <span className="brand-meta">· asha.dental</span>
          </div>
          <span className="pill">Setting Expectations</span>
        </div>

        {/* HERO */}
        <div className="hero">
          <div className="eyebrow">
            <b>Setting Expectations Guide</b> &nbsp;·&nbsp; AI-Powered Dental
            Practice Management
          </div>
          <h1 className="title">
            What to expect in your <em>first 12 weeks</em> and beyond
          </h1>
          <p className="title-sub">
            An honest look at calibration, safeguards, and the real-world edges.
          </p>
          <p className="lede">
            We'd rather set clear expectations up front than let surprises shape
            your view of the platform. This guide covers three things: what the
            first 12 weeks look like, how we handle the workflows where mistakes
            matter most, and what it is like to work with a fast-moving company
            after launch. It's candid by design — so you know exactly what
            partnering with Asha looks like.
          </p>

          <div className="meta-grid">
            <div className="meta-cell">
              <div className="meta-label">Calibration period</div>
              <div className="meta-value">
                12 weeks
                <small>to tune agents to your practice</small>
              </div>
            </div>
            <div className="meta-cell">
              <div className="meta-label">AM cadence</div>
              <div className="meta-value">
                2× / week
                <small>to drive adjustments and outcomes</small>
              </div>
            </div>
            <div className="meta-cell">
              <div className="meta-label">Critical workflows</div>
              <div className="meta-value">
                Safeguarded
                <small>extra checks, alerts, and human review</small>
              </div>
            </div>
            <div className="meta-cell">
              <div className="meta-label">Prepared by</div>
              <div className="meta-value">
                Asha Health, Inc.
                <small>sales@asha.health</small>
              </div>
            </div>
          </div>
        </div>

        {/* § 01 — FIRST 12 WEEKS */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 01</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">The first 12 weeks</h2>
          <p className="s-sub">
            The system works from day one, but the first 12 weeks are where it
            learns your practice's rules, preferences, and exceptions.
          </p>

          <p>
            Asha's agents can handle real work right away across scheduling,
            communications, billing, and more. What they don't know on day one
            is the nuance of <em className="accent">your</em> practice — your
            tone, your scheduling rules, your payer quirks, and the way your
            team prefers specific situations to be handled.
          </p>

          <p>
            During this period, your dedicated{" "}
            <strong>Account Manager meets with you twice a week</strong>. Those
            meetings are for making the system better quickly. The more you flag
            what looks off, the faster we can tune the rules and workflows to
            match how your office actually runs.
          </p>

          <blockquote className="pull">
            Think of it like onboarding a capable new team member: useful from
            the start, and sharper every week as they learn the way your practice
            works.
          </blockquote>

          <h3 className="sub">What may need adjustment</h3>

          <p>
            As your agents start handling real volume, a small number of cases
            may need correction. That could be a ledger update, an adjustment, a
            claim, or a patient workflow that the system handles differently than
            your team would have handled it.
          </p>

          <p>
            Some offices see none of this. Others see a handful of examples in
            the first few weeks. When one comes up, we add the rule, preference,
            or skill in the portal so the system handles that situation
            correctly the next time.
          </p>

          <h3 className="sub">Phone setup</h3>

          <p>
            For most offices, getting phones live takes about{" "}
            <strong>15–30 minutes</strong>. Rarely, a local internet, Wi-Fi, or
            port setting at the office can make that take several hours because
            the local network setup has to be fixed on site.
          </p>

          <p>
            To avoid surprises during the final phone-number transfer, we now
            move a test number first. That lets us catch local setup problems
            before the real cutover. For that test to work, the office needs
            someone available so we can validate the setup together.
          </p>
        </section>

        {/* § 02 — CRITICAL WORKFLOWS */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 02</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">Workflows where mistakes matter most</h2>
          <p className="s-sub">
            Patient messages, claims, posting, and payer work get extra checks
            because mistakes there have real consequences.
          </p>

          <p>
            Not every workflow carries the same risk. A small scheduling
            preference can be corrected quietly. A patient message, a claim, or
            a payment posting issue needs more care because it can affect the
            patient experience or your revenue.
          </p>

          <ul className="simple">
            <li>
              <b>Outbound AI communications.</b> Anything the system sends to
              your patients on your behalf gets extra review so tone, accuracy,
              and timing stay on track.
            </li>
            <li>
              <b>Revenue cycle work.</b> Claims, posting, and payer interactions
              are watched closely because small misses can turn into delayed
              payment.
            </li>
          </ul>

          <h3 className="sub">How we watch revenue cycle work</h3>

          <p>
            For revenue cycle work, we use a dashboard that watches for problems
            early instead of waiting for them to show up as missed or delayed
            payments. It looks for things like:
          </p>

          <ul className="simple">
            <li>
              <b>Claims still waiting on EOBs</b> — claims that have gone out
              but haven't come back with an explanation of benefits yet.
            </li>
            <li>
              <b>Payer payment-file setup</b> — whether electronic payment files
              are set up correctly across your payers.
            </li>
            <li>
              <b>EOB and payment posting mismatches</b> — differences between
              what was expected and what was posted.
            </li>
            <li>
              <b>Other payment interruptions</b> — anything that breaks the
              expected path from claim to payment.
            </li>
          </ul>

          <p>
            When the dashboard flags something, it becomes an action item for
            your Account Manager so it gets resolved quickly.
          </p>

          <h3 className="sub">Security and uptime</h3>

          <p>
            To date, Asha has had{" "}
            <strong>no security incidents and no data loss events</strong>. We
            operate at the highest levels of security and compliance, and protect
            your practice's data with that standard in mind across every
            workflow the platform touches.
          </p>

          <p>
            With an old local server, one office can go down by itself while
            everyone else keeps running. With Asha, that specific problem mostly
            goes away because the system runs in the cloud. Downtime is rare.
            When it happens, it is usually short-lived and tied to a broad AWS
            or internet outage affecting many services at once, not an isolated
            problem with your office.
          </p>
        </section>

        {/* § 03 — AFTER LAUNCH */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 03</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">After launch</h2>
          <p className="s-sub">
            We keep working with your team after launch: fixing issues, handling
            new requests, and improving the product.
          </p>

          <p>
            Asha launches new features fast, which is a big part of why the
            platform keeps getting better for your practice. The honest
            trade-off is that a fast launch can{" "}
            <strong>
              occasionally cause a specific PMS button or workflow to behave
              unexpectedly
            </strong>{" "}
            on the day it ships.
          </p>

          <p>
            When that happens, the fix is usually quick. Once an issue is
            reported, our team typically resolves it{" "}
            <em className="accent">the same day — often within the hour</em>.
            The best thing you can do is flag it as soon as you see it so we can
            turn it around fast.
          </p>

          <h3 className="sub">Custom requests</h3>

          <p>
            We can often{" "}
            <strong>
              accommodate custom workflow or product requests at no extra charge
            </strong>{" "}
            when the request fits within our product scope and would be useful
            to other practices too.
          </p>

          <p>
            The line we draw is product scope. A request like{" "}
            <em className="accent">AI for credentialing</em>, for example, falls
            outside what the platform is built to do, so it's not something we'd
            take on. But in-scope requests that improve the product for everyone
            are prioritized. When you ask for something that makes Asha better,
            you're often shaping the roadmap.
          </p>

          <h3 className="sub">Steady state</h3>

          <p>
            Once the first 12 weeks are behind you, agents are tuned to your
            practice, the most sensitive workflows are watched closely, and most
            day-to-day operations run with occasional review. New requests and
            the occasional edge case are handled through your portal and your
            ongoing relationship with your Account Manager.
          </p>

          <div className="faq-list">
            <div className="faq-item">
              <h3 className="faq-q">
                Will everything be perfect at the end of 12 weeks?
              </h3>
              <p className="faq-a">
                The 12-week period gets your agents calibrated to the vast
                majority of how your practice operates. Edge cases can still
                surface afterward. When they do, we add skills and rules to
                handle them. The goal is a system that's reliable and steadily
                improving, not one that's frozen in place.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="faq-q">
                What should I do when I spot something that looks wrong?
              </h3>
              <p className="faq-a">
                Flag it for your Account Manager. During calibration we review
                flagged cases in your twice-weekly sessions. For urgent issues
                like a PMS workflow problem on a launch day, reported issues are{" "}
                <strong>
                  typically fixed the same day, often within the hour
                </strong>
                .
              </p>
            </div>

            <div className="faq-item">
              <h3 className="faq-q">Can you build something custom for us?</h3>
              <p className="faq-a">
                Often, yes, as long as the request is within our product scope
                and would be useful to other practices as well. Requests well
                outside the platform's purpose, for example AI for credentialing,
                aren't something we take on.
              </p>
            </div>
          </div>
        </section>

        {/* TAIL */}
        <div className="tail">
          <span>Asha Health, Inc.</span>
          <span>Confidential to recipient</span>
          <span>www.asha.health · sales@asha.health</span>
        </div>
        </div>
      </div>
    </>
  );
};

export default ExpectationsGuidePage;
